import _ from 'lodash';
import React, { useEffect, useState } from 'react';
import WidgetPaper from '@components/widgets/display-widget/WidgetPaper';
import WidgetSection from '@components/widgets/display-widget/WidgetSection';
import SelectFormField from '@components/form-fields/SelectFormField';
import TextInputFormField from '@components/form-fields/TextInputFormField';
import { IAmendmentExpatDetails, IAmendmentPageState } from '..';
import { validateExpatDetailsField } from './validation';
import { IAmendmentGeneralData } from '../line-amendment';
import { useLazyQuery } from '@apollo/client';
import { getExpatLovsQuery } from '../queries';
import Loader from '@components/loader/Loader';
import { getExpatLovs } from '../utils';
import { useGridStyles } from '@styles/sharedStyles';

interface IExpatDetailsWidgetProps {
  generalData: IAmendmentGeneralData;
  pageState: IAmendmentPageState;
  onPageStateUpdate: (pageState: IAmendmentPageState) => void;
  isDisabled: boolean;
}

interface IExpatLovs {
  medicalClasses: Record<string, string>;
  emergencyOptions: Record<string, string>;
  continuityOptions: Record<string, string>;
}

const ExpatDetailsWidget: React.FC<IExpatDetailsWidgetProps> = ({
  pageState,
  onPageStateUpdate,
  isDisabled,
}) => {
  const { classes } = useGridStyles({
    fieldsPerRow: 3,
  });

  const [getExpatLovsLazy, { loading }] = useLazyQuery(getExpatLovsQuery);

  const values = pageState.values.expatDetails;
  const errors = pageState.errors.expatDetails;
  const touched = pageState.touched.expatDetails;

  const [lovs, setLovs] = useState<IExpatLovs>({
    continuityOptions: {},
    emergencyOptions: {},
    medicalClasses: {},
  });

  const initialize = async () => {
    const { data: lovsData } = await getExpatLovsLazy();

    const lists = getExpatLovs(lovsData);
    setLovs({
      medicalClasses: lists.medicalClasses,
      emergencyOptions: lists.emergencyOptions,
      continuityOptions: lists.continuityOptions,
    });
  };

  useEffect(() => {
    initialize();
  }, []);

  const onFieldBlur = async (fieldName: keyof IAmendmentExpatDetails) => {
    const newPageState = _.cloneDeep(pageState);
    newPageState.touched.expatDetails[fieldName] = true;

    onPageStateUpdate(newPageState);
  };

  const onFieldUpdate = (
    fieldName: keyof IAmendmentExpatDetails,
    value: unknown,
    touched = false
  ) => {
    const newPageState = _.cloneDeep(pageState);

    (newPageState.values.expatDetails as any)[fieldName] = value;

    newPageState.errors.expatDetails[fieldName] = validateExpatDetailsField(
      fieldName,
      value
    );

    if (touched) {
      newPageState.touched.expatDetails[fieldName] = true;
    }

    onPageStateUpdate(newPageState);
  };

  if (loading) {
    return <Loader />;
  }

  return (
    <WidgetPaper>
      <WidgetSection title="Expat Details" isFieldsGrid fieldsPerRow={3}>
        <SelectFormField
          key="medicalClass"
          name="medicalClass"
          title="Medical Class*"
          placeholder="Medical Class"
          value={values.medicalClass}
          error={touched.medicalClass ? errors.medicalClass : ''}
          onChange={(v) => onFieldUpdate('medicalClass', v, true)}
          disabled={isDisabled}
          selectOptions={lovs.medicalClasses}
          material
        />
        <SelectFormField
          key="continuity"
          name="continuity"
          title="Continuity"
          placeholder="Continuity"
          value={values.continuity}
          error={touched.continuity ? errors.continuity : ''}
          onChange={(v) => onFieldUpdate('continuity', v, true)}
          disabled={isDisabled}
          selectOptions={lovs.continuityOptions}
          material
        />

        <TextInputFormField
          key="continuityNumber"
          name="continuityNumber"
          title="Continuity Number"
          placeholder="Continuity Number"
          value={values.continuityNumber}
          error={touched.continuityNumber ? errors.continuityNumber : ''}
          onBlur={() => onFieldBlur('continuityNumber')}
          onChange={(v) => onFieldUpdate('continuityNumber', v.target.value)}
          disabled={isDisabled}
          material
        />

        <TextInputFormField
          key="exclusion"
          name="exclusion"
          title="Exclusion"
          placeholder="Exclusion"
          value={values.exclusion}
          error={touched.exclusion ? errors.exclusion : ''}
          onBlur={() => onFieldBlur('exclusion')}
          onChange={(v) => onFieldUpdate('exclusion', v.target.value)}
          disabled={isDisabled}
          classes={{
            labelWrapper: classes.fullWidthField,
          }}
          material
        />

        <TextInputFormField
          key="remarks"
          name="remarks"
          title="Remarks"
          placeholder="Remarks"
          value={values.remarks}
          error={touched.remarks ? errors.remarks : ''}
          onBlur={() => onFieldBlur('remarks')}
          onChange={(v) => onFieldUpdate('remarks', v.target.value)}
          disabled={isDisabled}
          classes={{
            labelWrapper: classes.fullWidthField,
          }}
          material
        />
      </WidgetSection>
    </WidgetPaper>
  );
};

export default ExpatDetailsWidget;
